import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Hash, MessageCircle, Send, Image as ImageIcon, Smile, Users,
  ArrowLeft, Plus, X, Search, Reply, Camera, ChevronDown, Settings, UserPlus, BellOff, Bell,
  Paperclip, Mic, Sparkles, MapPin, TrendingUp, ChevronRight, LogOut, Trash2, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import EditMemberModal from '../components/EditMemberModal';

const EMOJI_LIST = ['👍', '❤️', '😂', '🔥', '⛳', '🏌️', '💰', '🎉', '👏', '💪', '🍺', '🏆'];

export default function Chat() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // View state
  const [view, setView] = useState('list'); // 'list' | 'chat'
  const [activeTab, setActiveTab] = useState('groups'); // 'groups' (General Chat) | 'dms'

  // Data
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [activeDM, setActiveDM] = useState(null);
  const [messages, setMessages] = useState([]);
  const [dmConversations, setDmConversations] = useState([]);
  const [allMembers, setAllMembers] = useState([]);

  // Input
  const [newMessage, setNewMessage] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Modals
  const [showNewDM, setShowNewDM] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [searchMember, setSearchMember] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Create group
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  // @mention
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');

  // Muted channels
  const [mutedChannelIds, setMutedChannelIds] = useState(new Set());

  // Online Now + Member Info panel
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [memberProfile, setMemberProfile] = useState(null); // { user_id, ... } or null
  const [showMemberSheet, setShowMemberSheet] = useState(false);
  // Admin-only: opens the EditMemberModal when the admin taps the pencil in
  // the MemberInfoSheet. Null when the modal is closed.
  const [adminEditingMember, setAdminEditingMember] = useState(null);

  // Bundle AI icebreaker (canned suggestions for launch)
  const [dismissedAI, setDismissedAI] = useState(() => {
    try { return JSON.parse(localStorage.getItem('chat_ai_dismissed') || '{}'); }
    catch { return {}; }
  });

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchChannels();
      fetchDMConversations();
      fetchMembers();
      fetchMutedChannels();
      fetchRecentlyActive();
      // Heartbeat: keep last_active fresh so Online Now stays accurate
      fetch(`${API}/chat/heartbeat`, { method: 'POST', credentials: 'include' }).catch(() => {});
      const hbTimer = setInterval(() => {
        fetch(`${API}/chat/heartbeat`, { method: 'POST', credentials: 'include' }).catch(() => {});
        fetchRecentlyActive();
      }, 60000);
      return () => clearInterval(hbTimer);
    }
  }, [user]);

  const fetchRecentlyActive = async () => {
    try {
      const res = await fetch(`${API}/chat/recently-active?limit=12`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOnlineMembers(data.members || []);
      }
    } catch (_e) { /* silent */ }
  };

  const openMemberProfile = async (memberId) => {
    if (!memberId || memberId === user.user_id) return;
    setShowMemberSheet(true);
    setMemberProfile({ user_id: memberId, loading: true });
    try {
      const res = await fetch(`${API}/chat/member-profile/${memberId}`, { credentials: 'include' });
      if (res.ok) {
        setMemberProfile(await res.json());
      } else {
        setMemberProfile(null);
      }
    } catch (_e) {
      setMemberProfile(null);
    }
  };

  const dismissAI = (key) => {
    const next = { ...dismissedAI, [key]: Date.now() };
    setDismissedAI(next);
    try { localStorage.setItem('chat_ai_dismissed', JSON.stringify(next)); } catch (_e) { /* ignore */ }
  };

  // Deep-link: opening Chat with state.startDmWith auto-opens a DM with that user
  // and pre-fills the message draft. Used by the New Member Welcome card.
  useEffect(() => {
    const st = location.state;
    if (!user || !st?.startDmWith?.user_id) return;
    setActiveChannel(null);
    setActiveDM({ user_id: st.startDmWith.user_id, name: st.startDmWith.name });
    setView('chat');
    fetchDMMessages(st.startDmWith.user_id);
    if (st.prefillMessage) setNewMessage(st.prefillMessage);
    // Clear state so refresh doesn't re-trigger
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.state]);

  // Deep-link: /chat?channel=<id> from a chat notification click.
  // Waits until channels are loaded, then finds the matching one and opens it.
  useEffect(() => {
    if (!user || !channels?.length) return;
    const params = new URLSearchParams(location.search || '');
    const target = params.get('channel');
    if (!target) return;
    const match = channels.find(c => c.channel_id === target);
    if (match) {
      openChannel(match);
      // Strip the query param so a refresh/back doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete('channel');
      window.history.replaceState({}, document.title, url.pathname + url.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, channels, location.search]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll messages
  useEffect(() => {
    if (!activeChannel && !activeDM) return;
    const interval = setInterval(() => {
      if (activeChannel) fetchChannelMessages(activeChannel.channel_id, true);
      else if (activeDM) fetchDMMessages(activeDM.user_id, true);
    }, 4000);
    return () => clearInterval(interval);
  }, [activeChannel, activeDM]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/chat/channels`, { credentials: 'include' });
      if (res.ok) setChannels(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchChannelMessages = async (channelId, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/chat/channels/${channelId}/messages`, { credentials: 'include' });
      if (res.ok) setMessages(await res.json());
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoadingMessages(false); }
  };

  const fetchDMConversations = async () => {
    try {
      const res = await fetch(`${API}/chat/dms`, { credentials: 'include' });
      if (res.ok) setDmConversations(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchDMMessages = async (userId, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/chat/dms/${userId}/messages`, { credentials: 'include' });
      if (res.ok) setMessages(await res.json());
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoadingMessages(false); }
  };

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${API}/chat/members`, { credentials: 'include' });
      if (res.ok) setAllMembers(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchMutedChannels = async () => {
    try {
      const res = await fetch(`${API}/chat/muted-channels`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMutedChannelIds(new Set(data.muted_channel_ids || []));
      }
    } catch (e) { console.error(e); }
  };

  const toggleMute = async (channelId) => {
    try {
      const res = await fetch(`${API}/chat/channels/${channelId}/mute`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setMutedChannelIds(prev => {
          const next = new Set(prev);
          if (data.muted) next.add(channelId);
          else next.delete(channelId);
          return next;
        });
        toast.success(data.muted ? 'Channel muted' : 'Channel unmuted');
      }
    } catch { toast.error('Failed to update'); }
  };

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !imagePreview) return;

    let imageUrl = null;
    if (imagePreview) {
      imageUrl = await uploadImage(imagePreview.file);
      if (!imageUrl) return;
    }

    const endpoint = activeChannel
      ? `${API}/chat/channels/${activeChannel.channel_id}/messages`
      : `${API}/chat/dms/${activeDM.user_id}/messages`;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: newMessage,
          image_url: imageUrl,
          reply_to: replyTo?.message_id || null,
        })
      });
      if (res.ok) {
        setNewMessage('');
        setReplyTo(null);
        setImagePreview(null);
        if (activeChannel) fetchChannelMessages(activeChannel.channel_id);
        else { fetchDMMessages(activeDM.user_id); fetchDMConversations(); }
      }
    } catch (e) { toast.error('Failed to send message'); }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/chat/upload-image`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (res.ok) {
        const data = await res.json();
        return data.image_url;
      }
      toast.error('Upload failed');
      return null;
    } catch (e) { toast.error('Upload failed'); return null; }
    finally { setUploading(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview({ url: ev.target.result, file });
    reader.readAsDataURL(file);
  };

  const toggleReaction = async (messageId, emoji) => {
    try {
      await fetch(`${API}/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emoji })
      });
      if (activeChannel) fetchChannelMessages(activeChannel.channel_id, true);
      else fetchDMMessages(activeDM.user_id, true);
    } catch (e) { console.error(e); }
    setShowEmojiPicker(null);
  };

  // Create breakout group
  const createGroup = async () => {
    if (!newGroupName.trim()) { toast.error('Group name is required'); return; }
    try {
      const res = await fetch(`${API}/chat/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDesc,
          member_ids: selectedMembers.map(m => m.user_id),
        })
      });
      if (res.ok) {
        toast.success('Group created!');
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupDesc('');
        setSelectedMembers([]);
        fetchChannels();
      }
    } catch (e) { toast.error('Failed to create group'); }
  };

  // Navigation helpers
  const openChannel = (channel) => {
    setActiveChannel(channel);
    setActiveDM(null);
    setReplyTo(null);
    setView('chat');
    fetchChannelMessages(channel.channel_id);
    // Optimistically clear unread on this channel in the list
    setChannels(prev => prev.map(c => c.channel_id === channel.channel_id ? { ...c, unread_count: 0 } : c));
    // Mark channel as read on server
    fetch(`${API}/chat/mark-read`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel_id: channel.channel_id }),
    }).catch(() => {});
  };

  const openDM = (conv) => {
    setActiveDM(conv);
    setActiveChannel(null);
    setReplyTo(null);
    setView('chat');
    fetchDMMessages(conv.user_id);
    // Mark DMs as read
    fetch(`${API}/chat/mark-read`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel_id: 'dm' }),
    }).catch(() => {});
  };

  const startNewDM = (member) => {
    setActiveDM(member);
    setActiveChannel(null);
    setShowNewDM(false);
    setSearchMember('');
    setReplyTo(null);
    setView('chat');
    fetchDMMessages(member.user_id);
  };

  const goBack = () => {
    setView('list');
    setActiveChannel(null);
    setActiveDM(null);
    setReplyTo(null);
    setMessages([]);
  };

  // @mention handler
  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewMessage(val);
    const lastAt = val.lastIndexOf('@');
    if (lastAt !== -1 && lastAt === val.length - 1 || (lastAt !== -1 && !val.substring(lastAt).includes(' '))) {
      const query = val.substring(lastAt + 1);
      setMentionFilter(query);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (member) => {
    const lastAt = newMessage.lastIndexOf('@');
    const before = newMessage.substring(0, lastAt);
    setNewMessage(`${before}@${member.name} `);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredMentionMembers = allMembers.filter(m =>
    m.name.toLowerCase().includes(mentionFilter.toLowerCase())
  ).slice(0, 6);

  const filteredMembers = allMembers.filter(m =>
    m.name.toLowerCase().includes(searchMember.toLowerCase())
  );

  // Search / list filtering — must be BEFORE any early return per rules-of-hooks
  const [listQuery, setListQuery] = useState('');
  const filteredChannels = useMemo(() =>
    !listQuery ? channels : channels.filter(c => (c.name || '').toLowerCase().includes(listQuery.toLowerCase())),
    [channels, listQuery]
  );
  const filteredDMs = useMemo(() =>
    !listQuery ? dmConversations : dmConversations.filter(c => (c.name || '').toLowerCase().includes(listQuery.toLowerCase())),
    [dmConversations, listQuery]
  );

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const chatTitle = activeChannel ? `#${activeChannel.name}` : activeDM?.name || 'Chat';

  // ============================
  // RENDER: Conversation List
  // ============================
  const renderList = () => (
    <div className="flex flex-col h-full bg-white" data-testid="chat-list-view">
      {/* ────────── Hero ────────── */}
      <div className="bg-gradient-to-br from-bundle-green via-bundle-green to-bundle-green-dark text-white px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle className="w-5 h-5 text-bundle-gold" />
          <h1 className="font-heading font-black text-xl leading-none">Member Chat</h1>
        </div>
        <p className="font-body text-[12px] text-white/80 leading-snug">
          Private conversations. Golf groups. Real connections.
        </p>
      </div>

      {/* ────────── Search ────────── */}
      <div className="px-4 py-2.5 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={listQuery}
            onChange={(e) => setListQuery(e.target.value)}
            placeholder="Search members or conversations..."
            data-testid="chat-list-search"
            className="w-full h-9 pl-9 pr-3 rounded-full bg-bundle-cream/60 border border-transparent focus:border-bundle-green focus:bg-white focus:ring-1 focus:ring-bundle-green outline-none text-sm font-body text-gray-800 placeholder-gray-400 transition-all"
          />
        </div>
      </div>

      {/* ────────── Tabs ────────── */}
      <div className="flex border-b border-gray-200 bg-white">
        <button
          data-testid="tab-groups"
          onClick={() => setActiveTab('groups')}
          className={`flex-1 py-2.5 text-sm font-body font-bold transition-colors border-b-2 ${
            activeTab === 'groups'
              ? 'border-bundle-gold text-bundle-green'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Groups
        </button>
        <button
          data-testid="tab-dms"
          onClick={() => setActiveTab('dms')}
          className={`flex-1 py-2.5 text-sm font-body font-bold transition-colors border-b-2 ${
            activeTab === 'dms'
              ? 'border-bundle-gold text-bundle-green'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Direct Messages
        </button>
      </div>

      {/* ────────── Content ────────── */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'groups' && (
          <div>
            <button
              data-testid="create-group-btn"
              onClick={() => setShowCreateGroup(true)}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-bundle-cream/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-bundle-green/10 flex items-center justify-center ring-2 ring-bundle-gold/30">
                <Plus className="w-4 h-4 text-bundle-green" />
              </div>
              <div className="text-left flex-1">
                <div className="font-body text-sm font-bold text-bundle-green">New Group Chat</div>
                <div className="font-body text-[11px] text-gray-500">Foursome, event, or crew</div>
              </div>
            </button>

            {filteredChannels.map((ch) => (
              <button
                key={ch.channel_id}
                data-testid={`channel-${ch.channel_id}`}
                onClick={() => openChannel(ch)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-bundle-cream/50 transition-colors text-left"
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center relative flex-shrink-0 shadow-sm ${
                  ch.is_default ? 'bg-gradient-to-br from-bundle-green to-bundle-green-dark ring-2 ring-bundle-gold/40' : 'bg-gradient-to-br from-bundle-gold to-amber-600'
                }`}>
                  <Hash className="w-4 h-4 text-white" />
                  {ch.unread_count > 0 && (
                    <span
                      data-testid={`channel-unread-${ch.channel_id}`}
                      className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white shadow-md"
                    >
                      {ch.unread_count > 99 ? '99+' : ch.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-body text-sm text-gray-900 truncate ${ch.unread_count > 0 ? 'font-black' : 'font-bold'}`}>{ch.name}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {mutedChannelIds.has(ch.channel_id) && (
                        <BellOff className="w-3 h-3 text-gray-400" />
                      )}
                      {ch.last_time && (
                        <span className="font-body text-[10px] text-gray-400">
                          {formatTime(ch.last_time)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Users className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="font-body text-[10px] text-gray-500">
                      {ch.member_count ? `${ch.member_count} members` : (ch.is_default ? 'All members' : 'Group')}
                    </span>
                  </div>
                  <p className="font-body text-xs text-gray-500 truncate mt-0.5">
                    {ch.last_sender ? `${ch.last_sender}: ${ch.last_message}` : (ch.description || 'No messages yet — say hi 👋')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'dms' && (
          <div>
            {/* Online Now row — recently active members */}
            {onlineMembers.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-b from-bundle-cream/40 to-white">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <h3 className="font-heading font-black text-[11px] uppercase tracking-widest text-bundle-green">Online Now</h3>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
                  {onlineMembers.slice(0, 12).map(m => (
                    <button
                      key={m.user_id}
                      onClick={() => startNewDM(m)}
                      data-testid={`online-member-${m.user_id}`}
                      className="flex flex-col items-center gap-1 snap-start flex-shrink-0 w-14 group"
                    >
                      <div className="relative">
                        <Avatar name={m.name} picture={m.picture} size="md" />
                        {m.is_online && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white shadow-sm" />
                        )}
                      </div>
                      <span className="font-body text-[10px] font-semibold text-gray-700 truncate w-14 text-center group-hover:text-bundle-green">
                        {(m.name || '').split(' ')[0]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="new-dm-btn"
              onClick={() => setShowNewDM(true)}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-bundle-cream/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-full bg-bundle-green/10 flex items-center justify-center ring-2 ring-bundle-gold/30">
                <Plus className="w-4 h-4 text-bundle-green" />
              </div>
              <div className="text-left flex-1">
                <div className="font-body text-sm font-bold text-bundle-green">New Conversation</div>
                <div className="font-body text-[11px] text-gray-500">Message any Bundle Golf Club member</div>
              </div>
            </button>

            {/* Bundle AI icebreaker (canned for now, real GPT in Pass 2) */}
            {!dismissedAI.icebreaker_v1 && filteredDMs.length < 3 && (
              <div className="mx-4 my-3 rounded-2xl border border-bundle-gold/40 bg-gradient-to-br from-white via-bundle-cream/30 to-white shadow-sm overflow-hidden" data-testid="ai-icebreaker-card">
                <div className="p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-bundle-gold" />
                    <span className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green">Bundle AI</span>
                    <span className="ml-auto text-[9px] font-bold text-gray-400">SUGGESTION</span>
                  </div>
                  <p className="font-body text-xs text-gray-700 leading-snug mb-2">
                    Need help starting a conversation? Bundle AI can suggest an icebreaker based on shared Bundle Tags, courses, or tournaments.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowNewDM(true); dismissAI('icebreaker_v1'); }}
                      className="flex-1 py-1.5 rounded-full text-[11px] font-bold text-white bg-bundle-green hover:bg-bundle-green-dark"
                      data-testid="ai-icebreaker-use"
                    >
                      Try it
                    </button>
                    <button
                      onClick={() => dismissAI('icebreaker_v1')}
                      className="px-3 py-1.5 rounded-full text-[11px] font-bold text-gray-500 hover:bg-gray-100"
                      data-testid="ai-icebreaker-dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {filteredDMs.map((conv) => {
              const online = onlineMembers.find(m => m.user_id === conv.user_id);
              return (
                <button
                  key={conv.user_id}
                  data-testid={`dm-${conv.user_id}`}
                  onClick={() => openDM(conv)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-bundle-cream/50 transition-colors text-left"
                >
                  <div className="relative flex-shrink-0">
                    <Avatar name={conv.name} picture={conv.picture} size="lg" />
                    {online?.is_online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white shadow-sm" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-body text-sm text-gray-900 truncate ${conv.unread_count > 0 ? 'font-black' : 'font-bold'}`}>{conv.name}</span>
                      {conv.last_time && (
                        <span className="font-body text-[10px] text-gray-400 flex-shrink-0">
                          {formatTime(conv.last_time)}
                        </span>
                      )}
                    </div>
                    <p className={`font-body text-xs truncate mt-0.5 ${conv.unread_count > 0 ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
                      {conv.last_message || 'Start the conversation...'}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-bundle-green text-white text-[10px] font-black flex items-center justify-center flex-shrink-0 shadow-sm">
                      {conv.unread_count > 99 ? '99+' : conv.unread_count}
                    </span>
                  )}
                </button>
              );
            })}

            {filteredDMs.length === 0 && !listQuery && (
              <div className="p-8 text-center" data-testid="dm-empty-state">
                <div className="w-16 h-16 rounded-full bg-bundle-green/10 mx-auto mb-3 flex items-center justify-center ring-4 ring-bundle-gold/20">
                  <MessageCircle className="w-8 h-8 text-bundle-green" />
                </div>
                <h3 className="font-heading font-black text-base text-bundle-green mb-1">Your Golf Network Is Waiting</h3>
                <p className="font-body text-xs text-gray-500 leading-snug max-w-[240px] mx-auto mb-4">
                  Start a conversation with someone you&rsquo;ve met through Bundle Golf Club.
                </p>
                <div className="flex flex-col gap-2 max-w-[220px] mx-auto">
                  <button
                    onClick={() => setShowNewDM(true)}
                    data-testid="empty-start-conversation"
                    className="w-full py-2 rounded-full text-xs font-bold text-white bg-bundle-green hover:bg-bundle-green-dark shadow-sm"
                  >
                    Start a Conversation
                  </button>
                  <button
                    onClick={() => navigate('/members')}
                    className="w-full py-2 rounded-full text-xs font-bold text-bundle-green border border-bundle-green/40 hover:bg-bundle-green/5"
                  >
                    Explore Members
                  </button>
                  <button
                    onClick={() => navigate('/golfers-near-me')}
                    className="w-full py-2 rounded-full text-xs font-bold text-bundle-green border border-bundle-green/40 hover:bg-bundle-green/5"
                  >
                    Find a Golfer
                  </button>
                </div>
              </div>
            )}

            {filteredDMs.length === 0 && listQuery && (
              <p className="text-center text-gray-400 font-body text-sm py-8">No conversations match &ldquo;{listQuery}&rdquo;</p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============================
  // RENDER: Chat View
  // ============================
  const renderChat = () => (
    <div className="flex flex-col h-full bg-white" data-testid="chat-view">
      {/* Chat Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10 shadow-sm">
        <button
          data-testid="chat-back-btn"
          onClick={goBack}
          className="md:hidden p-1 -ml-1 text-bundle-green"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        {activeChannel ? (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-bundle-green to-bundle-green-dark flex items-center justify-center flex-shrink-0 ring-2 ring-bundle-gold/40 shadow-sm">
            <Hash className="w-4 h-4 text-white" />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openMemberProfile(activeDM?.user_id)}
            data-testid="chat-header-avatar"
            className="flex-shrink-0"
            aria-label="View member profile"
          >
            <Avatar name={activeDM?.name} picture={activeDM?.picture} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          {activeChannel ? (
            <>
              <h2 className="font-heading font-black text-sm text-bundle-green truncate">{chatTitle}</h2>
              <p className="font-body text-[11px] text-gray-500 truncate">
                {activeChannel.description || 'Group chat'}
              </p>
            </>
          ) : (
            <button
              type="button"
              onClick={() => openMemberProfile(activeDM?.user_id)}
              className="text-left w-full"
            >
              <h2 className="font-heading font-black text-sm text-bundle-green truncate">{chatTitle}</h2>
              <p className="font-body text-[11px] text-gray-500 truncate">Tap for profile</p>
            </button>
          )}
        </div>
        {/* Mute toggle for channels */}
        {activeChannel && (
          <button
            data-testid="chat-mute-toggle"
            onClick={() => toggleMute(activeChannel.channel_id)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={mutedChannelIds.has(activeChannel.channel_id) ? 'Unmute channel' : 'Mute channel'}
          >
            {mutedChannelIds.has(activeChannel.channel_id) ? (
              <BellOff className="w-4 h-4 text-gray-400" />
            ) : (
              <Bell className="w-4 h-4 text-bundle-green" />
            )}
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50/50" data-testid="messages-area">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bundle-green"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
            <p className="font-body text-sm">No messages yet</p>
            <p className="font-body text-xs mt-1">Be the first to say something!</p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((msg, idx) => {
              const isMe = msg.sender_id === user.user_id;
              const showAvatar = idx === 0 || messages[idx - 1].sender_id !== msg.sender_id;
              const showName = showAvatar && !isMe;

              return (
                <div key={msg.message_id} data-testid={`msg-${msg.message_id}`} className="group">
                  {showAvatar && <div className={idx > 0 ? 'mt-3' : ''} />}
                  <div className={`flex items-start gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
                    {/* Avatar */}
                    <div className="w-8 flex-shrink-0">
                      {showAvatar && (
                        <button
                          type="button"
                          onClick={() => openMemberProfile(msg.sender_id)}
                          className="focus:outline-none"
                          aria-label={`View ${msg.sender_name}'s profile`}
                        >
                          <Avatar name={msg.sender_name} picture={msg.sender_picture} size="sm" />
                        </button>
                      )}
                    </div>

                    {/* Bubble */}
                    <div className={`max-w-[75%] min-w-[60px] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                      {showName && (
                        <span className="font-body text-[11px] font-semibold text-gray-600 mb-0.5 ml-1">{msg.sender_name}</span>
                      )}

                      {/* Reply preview */}
                      {msg.reply_to_content && (
                        <div className={`text-[11px] font-body px-2 py-1 mb-0.5 rounded border-l-2 border-bundle-gold bg-bundle-gold/5 max-w-full truncate ${isMe ? 'ml-auto' : ''}`}>
                          <span className="font-semibold text-bundle-green">{msg.reply_to_sender}</span>
                          <span className="text-gray-500 ml-1">{msg.reply_to_content}</span>
                        </div>
                      )}

                      <div
                        className={`px-3 py-2 rounded-2xl relative ${
                          isMe
                            ? 'bg-bundle-green text-white rounded-tr-sm'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm'
                        }`}
                      >
                        {/* Render content with @mentions highlighted */}
                        <p className="font-body text-sm break-words whitespace-pre-wrap">
                          {renderMessageContent(msg.content, isMe)}
                        </p>
                        {msg.image_url && (
                          <img
                            src={msg.image_url.startsWith('/api') ? `${window.location.origin}${msg.image_url}` : msg.image_url}
                            alt=""
                            className="mt-1.5 rounded-lg max-w-full max-h-48 object-cover cursor-pointer"
                            onClick={() => window.open(msg.image_url.startsWith('/api') ? `${window.location.origin}${msg.image_url}` : msg.image_url, '_blank')}
                          />
                        )}
                        <span className={`font-body text-[10px] mt-0.5 block ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>

                      {/* Reactions row */}
                      {Object.keys(msg.reactions || {}).length > 0 && (
                        <div className={`flex items-center gap-1 mt-0.5 flex-wrap ${isMe ? 'justify-end' : ''}`}>
                          {Object.entries(msg.reactions).map(([emoji, users]) => (
                            <button
                              key={emoji}
                              onClick={() => toggleReaction(msg.message_id, emoji)}
                              className={`px-1.5 py-0.5 rounded-full text-xs flex items-center gap-0.5 ${
                                users.includes(user.user_id)
                                  ? 'bg-bundle-gold/30 border border-bundle-gold'
                                  : 'bg-white border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <span>{emoji}</span>
                              <span className="font-body text-[10px]">{users.length}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Action bar (visible on hover/long press) */}
                      <div className={`flex items-center gap-1 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${isMe ? 'justify-end' : ''}`}>
                        <button
                          data-testid={`reply-${msg.message_id}`}
                          onClick={() => setReplyTo(msg)}
                          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                          title="Reply"
                        >
                          <Reply className="w-3.5 h-3.5" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setShowEmojiPicker(showEmojiPicker === msg.message_id ? null : msg.message_id)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                            title="React"
                          >
                            <Smile className="w-3.5 h-3.5" />
                          </button>
                          {showEmojiPicker === msg.message_id && (
                            <div className={`absolute ${isMe ? 'right-0' : 'left-0'} bottom-full mb-1 bg-white rounded-xl shadow-xl border border-gray-200 p-2 flex gap-1 flex-wrap w-52 z-20`}>
                              {EMOJI_LIST.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => toggleReaction(msg.message_id, emoji)}
                                  className="p-1.5 hover:bg-gray-100 rounded-lg text-base transition-transform hover:scale-110"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Reply bar */}
      {replyTo && (
        <div className="px-4 py-2 bg-bundle-gold/10 border-t border-bundle-gold/20 flex items-center gap-2">
          <Reply className="w-4 h-4 text-bundle-gold flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-body text-xs font-semibold text-bundle-green">{replyTo.sender_name}</span>
            <p className="font-body text-xs text-gray-500 truncate">{replyTo.content}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Image preview */}
      {imagePreview && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-3">
          <img src={imagePreview.url} alt="" className="w-16 h-16 rounded-lg object-cover" />
          <span className="font-body text-xs text-gray-500 flex-1">Image attached</span>
          <button onClick={() => setImagePreview(null)} className="text-gray-400 hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* @Mention dropdown */}
      {showMentions && filteredMentionMembers.length > 0 && (
        <div className="mx-4 mb-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-h-48 overflow-y-auto z-20">
          {filteredMentionMembers.map(m => (
            <button
              key={m.user_id}
              onClick={() => insertMention(m)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-bundle-cream/50 text-left transition-colors"
            >
              <Avatar name={m.name} picture={m.picture} size="xs" />
              <span className="font-body text-sm text-gray-800">{m.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      {(activeChannel || activeDM) && (
        <form onSubmit={sendMessage} className="px-3 py-2 border-t border-gray-200 bg-white" data-testid="chat-input-form">
          <div className="flex items-end gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-400 hover:text-bundle-green transition-colors flex-shrink-0 rounded-full hover:bg-bundle-cream/60"
              data-testid="attach-image-btn"
              title="Attach a photo"
              aria-label="Attach a photo"
            >
              <Camera className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => toast.info('Voice messages are coming soon 🎤')}
              className="p-2 text-gray-400 hover:text-bundle-green transition-colors flex-shrink-0 rounded-full hover:bg-bundle-cream/60 hidden sm:inline-flex"
              data-testid="voice-message-btn"
              title="Voice message (coming soon)"
              aria-label="Voice message"
            >
              <Mic className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                data-testid="chat-message-input"
                value={newMessage}
                onChange={handleInputChange}
                placeholder={`Type a message${activeDM?.name ? ' to ' + (activeDM.name.split(' ')[0]) : ''}...`}
                className="pl-4 pr-10 border-gray-200 focus:border-bundle-green rounded-full bg-bundle-cream/40 focus:bg-white text-sm h-10"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowEmojiPicker(showEmojiPicker === 'composer' ? null : 'composer')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-bundle-green rounded-full transition-colors"
                data-testid="composer-emoji-btn"
                aria-label="Insert emoji"
              >
                <Smile className="w-5 h-5" />
              </button>
              {showEmojiPicker === 'composer' && (
                <div className="absolute right-0 bottom-full mb-2 bg-white rounded-xl shadow-xl border border-gray-200 p-2 flex gap-1 flex-wrap w-56 z-30">
                  {EMOJI_LIST.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => { setNewMessage((m) => m + emoji); setShowEmojiPicker(null); inputRef.current?.focus(); }}
                      className="p-1.5 hover:bg-bundle-cream/60 rounded-lg text-base transition-transform hover:scale-110"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              data-testid="send-message-btn"
              disabled={(!newMessage.trim() && !imagePreview) || uploading}
              size="icon"
              className="bg-bundle-green hover:bg-bundle-green-dark text-white rounded-full w-10 h-10 flex-shrink-0 shadow-sm"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      )}
    </div>
  );

  // ============================
  // MAIN LAYOUT
  // ============================
  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-0">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-5xl mx-auto pt-16 md:pt-20 h-screen md:h-[calc(100vh-0px)]">
        {/* Desktop: side-by-side */}
        <div className="hidden md:flex h-[calc(100vh-80px)] bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg mx-4">
          {/* Sidebar */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            {renderList()}
          </div>
          {/* Chat area */}
          <div className="flex-1 flex flex-col">
            {activeChannel || activeDM ? renderChat() : (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-gray-200 mx-auto mb-3" />
                  <p className="font-body text-gray-400 text-sm">Select a conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: switch between list and chat */}
        <div className="md:hidden flex flex-col h-[calc(100vh-56px-72px)] bg-white">
          {view === 'list' ? renderList() : renderChat()}
        </div>
      </div>

      {/* New DM Modal */}
      {showNewDM && (
        <Modal onClose={() => setShowNewDM(false)} title="New Message">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="dm-search-input"
              value={searchMember}
              onChange={(e) => setSearchMember(e.target.value)}
              placeholder="Search members..."
              className="pl-9 border-gray-200 focus:border-bundle-green rounded-full text-sm"
            />
          </div>
          <div className="max-h-72 overflow-y-auto space-y-0.5">
            {filteredMembers.map((m) => (
              <button
                key={m.user_id}
                data-testid={`dm-member-${m.user_id}`}
                onClick={() => startNewDM(m)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bundle-cream/50 transition-colors"
              >
                <Avatar name={m.name} picture={m.picture} />
                <span className="font-body text-sm text-gray-800">{m.name}</span>
              </button>
            ))}
            {filteredMembers.length === 0 && (
              <p className="text-center text-gray-500 font-body text-sm py-6">No members found</p>
            )}
          </div>
        </Modal>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <Modal onClose={() => setShowCreateGroup(false)} title="New Group Chat">
          <div className="space-y-3">            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Group Name</label>
              <Input
                data-testid="group-name-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g. Saturday Foursome"
                className="border-gray-200 focus:border-bundle-green text-sm"
              />
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">Description (optional)</label>
              <Input
                data-testid="group-desc-input"
                value={newGroupDesc}
                onChange={(e) => setNewGroupDesc(e.target.value)}
                placeholder="What's this group for?"
                className="border-gray-200 focus:border-bundle-green text-sm"
              />
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-600 block mb-1">
                Add Members ({selectedMembers.length})
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchMember}
                  onChange={(e) => setSearchMember(e.target.value)}
                  placeholder="Search members..."
                  className="pl-9 border-gray-200 focus:border-bundle-green rounded-full text-sm"
                />
              </div>
              {/* Selected members chips */}
              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedMembers.map(m => (
                    <span key={m.user_id} className="inline-flex items-center gap-1 px-2 py-1 bg-bundle-green/10 text-bundle-green rounded-full text-xs font-body">
                      {m.name}
                      <button onClick={() => setSelectedMembers(prev => prev.filter(p => p.user_id !== m.user_id))}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {allMembers.filter(m =>
                  m.name.toLowerCase().includes(searchMember.toLowerCase()) &&
                  !selectedMembers.find(s => s.user_id === m.user_id)
                ).map(m => (
                  <button
                    key={m.user_id}
                    onClick={() => setSelectedMembers(prev => [...prev, m])}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-bundle-cream/50 transition-colors"
                  >
                    <Avatar name={m.name} picture={m.picture} size="sm" />
                    <span className="font-body text-sm text-gray-800">{m.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <Button
              data-testid="create-group-submit"
              onClick={createGroup}
              className="w-full btn-bundle"
              disabled={!newGroupName.trim()}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create Group
            </Button>
          </div>
        </Modal>
      )}

      {/* Member Info Sheet */}
      {showMemberSheet && (
        <MemberInfoSheet
          profile={memberProfile}
          onClose={() => { setShowMemberSheet(false); setMemberProfile(null); }}
          onViewProfile={(uid) => { setShowMemberSheet(false); navigate(`/members?open=${uid}`); }}
          onDM={(m) => { setShowMemberSheet(false); startNewDM(m); }}
          onEditAsAdmin={(m) => { setShowMemberSheet(false); setAdminEditingMember(m); }}
          currentUserId={user?.user_id}
          currentUserIsAdmin={!!user?.is_admin}
        />
      )}

      {/* Admin — edit any member's profile from the Chat member sheet. Same
          modal as the Admin panel; writes to admin_audit_log server-side. */}
      <EditMemberModal
        member={adminEditingMember}
        open={!!adminEditingMember}
        onClose={() => setAdminEditingMember(null)}
        onSaved={(fresh) => {
          setAdminEditingMember(null);
          // Refresh the currently-open member sheet if it was the same person
          if (memberProfile?.user_id === fresh.user_id) {
            setMemberProfile((p) => ({ ...(p || {}), ...fresh }));
          }
        }}
      />
    </div>
  );
}

// ============================
// HELPER COMPONENTS
// ============================

function Avatar({ name, picture, size = 'md' }) {
  const sizes = { xs: 'w-6 h-6 text-[10px]', sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-12 h-12 text-base' };
  if (picture) return <img src={picture} alt="" className={`${sizes[size]} rounded-full object-cover flex-shrink-0`} />;
  return (
    <div className={`${sizes[size]} rounded-full bg-bundle-gold flex items-center justify-center flex-shrink-0`}>
      <span className="font-bold text-bundle-green-dark">{name?.[0]?.toUpperCase() || '?'}</span>
    </div>
  );
}

function Modal({ onClose, title, children }) {  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white w-full md:max-w-md md:rounded-xl rounded-t-2xl max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="font-heading font-semibold text-bundle-green text-base">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function formatTime(dateStr) {  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function renderMessageContent(content, isMe) {
  // Highlight @mentions
  const parts = content.split(/(@\w[\w\s]*?)(?=\s@|\s|$)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className={`font-semibold ${isMe ? 'text-bundle-gold' : 'text-bundle-green'}`}>
          {part}
        </span>
      );
    }
    return part;
  });
}

function MemberInfoSheet({ profile, onClose, onViewProfile, onDM, onEditAsAdmin, currentUserId, currentUserIsAdmin }) {
  const loading = !profile || profile.loading;
  const isSelf = profile?.user_id === currentUserId;
  const canAdminEdit = currentUserIsAdmin && !isSelf && !loading && profile?.user_id;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white w-full md:max-w-sm md:rounded-2xl rounded-t-3xl max-h-[80vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="member-info-sheet"
      >
        {/* Golf-club hero strip */}
        <div className="relative bg-gradient-to-br from-bundle-green via-bundle-green to-bundle-green-dark px-4 pt-4 pb-10">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          {canAdminEdit && (
            <button
              onClick={() => onEditAsAdmin && onEditAsAdmin(profile)}
              className="absolute top-3 right-12 p-1.5 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center gap-1"
              aria-label="Edit member as admin"
              data-testid="member-sheet-admin-edit"
              title="Edit as admin"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Edit</span>
            </button>
          )}
          <div className="flex items-center gap-1.5 text-bundle-gold text-[10px] font-black uppercase tracking-widest">
            <span className="w-1 h-1 rounded-full bg-bundle-gold" />
            Bundle Golf Club Member
          </div>
        </div>

        <div className="px-4 -mt-8 pb-5">
          {/* Avatar overlaps the hero */}
          <div className="flex items-end justify-between mb-3">
            <div className="ring-4 ring-white rounded-full shadow-md">
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-bundle-gold flex items-center justify-center">
                  <span className="font-heading font-black text-bundle-green-dark text-lg">
                    {(profile?.name || '?')[0]?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {profile?.last_active && (
              <span className="font-body text-[10px] text-gray-500 mb-1">
                Active {formatTime(profile.last_active)}
              </span>
            )}
          </div>

          {loading ? (
            <div className="py-4">
              <div className="h-4 w-40 bg-gray-100 rounded animate-pulse mb-2" />
              <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
            </div>
          ) : (
            <>
              <h3 className="font-heading font-black text-lg text-bundle-green leading-tight">{profile.name || 'Member'}</h3>
              {(profile.location || profile.home_course) && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <MapPin className="w-3 h-3" />
                  <span>{profile.location || profile.home_course}</span>
                </div>
              )}
              {profile.handicap_index !== null && profile.handicap_index !== undefined && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <TrendingUp className="w-3 h-3" />
                  <span>Handicap {profile.handicap_index}</span>
                </div>
              )}
              {Array.isArray(profile.bundle_tags) && profile.bundle_tags.length > 0 && (
                <div className="mt-3">
                  <p className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green mb-1.5">
                    Bundle Tags
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.bundle_tags.slice(0, 8).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-bundle-cream border border-bundle-gold/40 text-[11px] font-body font-semibold text-bundle-green"
                      >
                        #{typeof tag === 'string' ? tag.replace(/^#/, '') : tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!isSelf && (
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    onClick={() => onViewProfile(profile.user_id)}
                    data-testid="member-sheet-view-profile"
                    className="w-full py-2.5 rounded-full text-sm font-bold text-white bg-bundle-green hover:bg-bundle-green-dark shadow-sm flex items-center justify-center gap-1.5"
                  >
                    View Profile
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDM(profile)}
                    data-testid="member-sheet-connect"
                    className="w-full py-2.5 rounded-full text-sm font-bold text-bundle-green border border-bundle-green/40 hover:bg-bundle-green/5"
                  >
                    Send a message
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
